<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-custom-blue">Lab Equipment Register</h1>
      <button @click="openAdd" class="px-4 py-2 bg-custom-yellow text-white text-sm rounded hover:bg-orange-600">
        + Add Equipment
      </button>
    </div>

    <!-- Add / Edit Form -->
    <div v-if="showForm" class="mb-6 p-5 border border-gray-200 rounded-lg bg-gray-50">
      <h2 class="text-sm font-semibold text-gray-700 mb-4">{{ editingId ? 'Edit Equipment' : 'New Equipment' }}</h2>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Name *</label>
          <input v-model="form.name" type="text" placeholder="e.g. Centrifuge #1"
            class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Type *</label>
          <select v-model="form.equipment_type" class="w-full border border-gray-300 rounded p-2 text-sm bg-white">
            <option value="">— select —</option>
            <option v-for="t in EQUIPMENT_TYPES" :key="t" :value="t">{{ label(t) }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <select v-model="form.status" class="w-full border border-gray-300 rounded p-2 text-sm bg-white">
            <option value="operational">Operational</option>
            <option value="under_maintenance">Under Maintenance</option>
            <option value="decommissioned">Decommissioned</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Make / Brand</label>
          <input v-model="form.make" type="text" class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Model</label>
          <input v-model="form.model" type="text" class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Serial Number</label>
          <input v-model="form.serial_number" type="text" class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Location</label>
          <input v-model="form.location" type="text" placeholder="e.g. Bench A"
            class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Acquisition Date</label>
          <input v-model="form.acquisition_date" type="date" class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Warranty Expiry</label>
          <input v-model="form.warranty_expiry" type="date" class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
        </div>
        <div class="md:col-span-3">
          <label class="block text-xs font-medium text-gray-600 mb-1">Notes</label>
          <textarea v-model="form.notes" rows="2" class="w-full border border-gray-300 rounded p-2 text-sm bg-white"></textarea>
        </div>
      </div>
      <div class="flex justify-end gap-3 mt-4">
        <button @click="cancelForm" class="px-4 py-2 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-100">
          Cancel
        </button>
        <button @click="saveEquipment" :disabled="saving"
          class="px-5 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-50">
          {{ saving ? 'Saving…' : (editingId ? 'Update' : 'Add Equipment') }}
        </button>
      </div>
    </div>

    <!-- Table (hidden while form is open) -->
    <template v-if="!showForm">
      <div v-if="loading" class="flex justify-center py-16">
        <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-custom-yellow"></div>
      </div>
      <div v-else class="overflow-x-auto shadow-sm rounded-sm">
        <table class="w-full text-left text-gray-700 text-sm">
          <thead>
            <tr class="bg-gray-100">
              <th class="py-3 px-4">Name</th>
              <th class="py-3 px-4">Type</th>
              <th class="py-3 px-4">Make / Model</th>
              <th class="py-3 px-4">Serial No.</th>
              <th class="py-3 px-4">Location</th>
              <th class="py-3 px-4">Status</th>
              <th class="py-3 px-4">Last Maint.</th>
              <th class="py-3 px-4">Last Calib.</th>
              <th class="py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody class="font-light text-gray-600">
            <tr v-if="equipment.length === 0">
              <td colspan="9" class="py-8 text-center text-gray-400">No equipment registered yet.</td>
            </tr>
            <tr v-for="e in equipment" :key="e.id" class="border-b border-gray-200 hover:bg-gray-50">
              <td class="py-3 px-4 font-medium text-gray-800">{{ e.name }}</td>
              <td class="py-3 px-4">{{ label(e.equipment_type) }}</td>
              <td class="py-3 px-4">{{ [e.make, e.model].filter(Boolean).join(' / ') || '—' }}</td>
              <td class="py-3 px-4">{{ e.serial_number || '—' }}</td>
              <td class="py-3 px-4">{{ e.location || '—' }}</td>
              <td class="py-3 px-4">
                <span class="px-2 py-0.5 rounded-full text-xs font-medium" :class="statusClass(e.status)">
                  {{ statusLabel(e.status) }}
                </span>
              </td>
              <td class="py-3 px-4 text-xs">{{ e.last_maintenance?.performed_at || '—' }}</td>
              <td class="py-3 px-4 text-xs">{{ e.last_calibration?.performed_at || '—' }}</td>
              <td class="py-3 px-4">
                <div class="flex gap-2">
                  <button @click="$router.push(`/lab/equipment/${e.id}`)"
                    class="px-3 py-1 text-xs border border-custom-blue text-custom-blue rounded hover:bg-blue-50">
                    View
                  </button>
                  <button @click="openEdit(e)"
                    class="px-3 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-100">
                    Edit
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import apiClient from '@/services/api'

const BLANK_FORM = () => ({
  name: '', equipment_type: '', make: '', model: '', serial_number: '',
  location: '', acquisition_date: '', warranty_expiry: '', status: 'operational', notes: '',
})

export default {
  name: 'LabEquipmentPage',
  data() {
    return {
      equipment:  [],
      loading:    true,
      showForm:   false,
      saving:     false,
      editingId:  null,
      form:       BLANK_FORM(),
      EQUIPMENT_TYPES: [
        'centrifuge','microscope','glucometer','spectrophotometer',
        'refrigerator','incubator','autoclave','pipette','analyser','other',
      ],
    }
  },
  async mounted() {
    await this.loadEquipment()
  },
  methods: {
    async loadEquipment() {
      this.loading = true
      try {
        const res = await apiClient.listLabEquipment()
        if (res.success && Array.isArray(res.data)) this.equipment = res.data
      } catch (e) {
        console.error('Failed to load equipment', e)
      } finally {
        this.loading = false
      }
    },
    openAdd() {
      this.editingId = null
      this.form = BLANK_FORM()
      this.showForm = true
    },
    openEdit(e) {
      this.editingId = e.id
      this.form = {
        name: e.name, equipment_type: e.equipment_type, make: e.make || '',
        model: e.model || '', serial_number: e.serial_number || '',
        location: e.location || '', acquisition_date: e.acquisition_date || '',
        warranty_expiry: e.warranty_expiry || '', status: e.status, notes: e.notes || '',
      }
      this.showForm = true
    },
    cancelForm() {
      this.showForm = false
      this.editingId = null
      this.form = BLANK_FORM()
    },
    async saveEquipment() {
      if (!this.form.name || !this.form.equipment_type) {
        this.$toast.error('Name and type are required')
        return
      }
      this.saving = true
      try {
        const res = this.editingId
          ? await apiClient.updateLabEquipment(this.editingId, this.form)
          : await apiClient.createLabEquipment(this.form)
        if (res.success) {
          this.$toast.success(this.editingId ? 'Equipment updated' : 'Equipment added')
          this.cancelForm()
          await this.loadEquipment()
        } else {
          this.$toast.error(res.message || 'Failed to save')
        }
      } catch (e) {
        this.$toast.error('Failed to save equipment')
      } finally { 
        this.saving = false
      }
    },
    label(type) {
      return type ? type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ') : '—'
    },
    statusLabel(s) {
      return { operational: 'Operational', under_maintenance: 'Under Maintenance', decommissioned: 'Decommissioned' }[s] || s
    },
    statusClass(s) {
      return {
        operational:       'bg-green-100 text-green-800',
        under_maintenance: 'bg-amber-100 text-amber-800',
        decommissioned:    'bg-gray-100 text-gray-500',
      }[s] || 'bg-gray-100 text-gray-500'
    },
  },
}
</script>
